import { filter, get, isDate, isEmpty, isNumber, isString, map, set, uniq } from 'lodash';
import { fromJS } from 'immutable';
import moment from 'moment';
import * as Yup from 'yup';
import { getTermsIncluded } from 'ui-utils';

import { Country } from '@lib/providers/LanguageProvider';

// using the /max instace to add landlines verification
import { parsePhoneNumberFromString as parsePhoneNumber } from 'libphonenumber-js/max';

import { CHINESE_LANGUAGE_ID, TRADITIONAL_CHINESE_LANGUAGE_ID } from 'containers/PatientPage/constants';
import { isInvalidEmail } from 'utils/validators';

const join = (rules) => (value, nestedData, data, props) =>
  rules.map((rule) => rule(value, nestedData, data, props)).filter((error) => !!error)[0];

export function required(value) {
  // WORKAROUND numbers and dates return true for isEmpty
  // https://github.com/lodash/lodash/issues/483

  let newValue = value;
  if (isString(value)) {
    newValue = value.trim();
  }

  if (isEmpty(newValue) && !isNumber(newValue) && !isDate(newValue)) {
    return 'Required';
  }
  return false;
}

export function requiredFieldCustom(field, message) {
  return (value, formValues) => {
    const fieldValue = get(formValues, field);
    if (isEmpty(fieldValue) && !isNumber(fieldValue) && !isDate(fieldValue)) {
      return message || 'Required';
    }
    return false;
  };
}

export function requiredAtLeastOne(keys) {
  return (value, data) => {
    const fields = fromJS(data).toJS();

    return !keys.some((key) => !required(get(fields, key))) ? 'Required' : false;
  };
}

export function requiredCulture() {
  return (value, data) => {
    const fields = fromJS(data).toJS();

    return (fields?.language === CHINESE_LANGUAGE_ID || fields?.language === TRADITIONAL_CHINESE_LANGUAGE_ID) && !value
      ? 'Required'
      : false;
  };
}

export function syncTimeGreaterThan(key) {
  return (value, data) => {
    const fields = fromJS(data).toJS();
    const field = get(fields, key);

    if ((value || 0) <= (field || 0)) return 'Must be greater than the start time';

    return false;
  };
}

export function syncTimeLowerThan(key) {
  return (value, data) => {
    const fields = fromJS(data).toJS();
    const field = get(fields, key);

    if ((value || 0) >= (field || 0)) return 'Must be lower than the end time';

    return false;
  };
}

export function validateMobileNumber(value, country, invalidPhoneAlert) {
  if (!value || isEmpty(value) || !isString(value)) return false;

  let phoneNumber;
  if (country) {
    phoneNumber = parsePhoneNumber(value, country);
  } else {
    // Default to US > BR
    phoneNumber = parsePhoneNumber(value, 'US');
    if (!phoneNumber || !phoneNumber.isValid()) {
      phoneNumber = parsePhoneNumber(value, 'BR');
    }
  }
  return phoneNumber && phoneNumber.isValid() ? false : invalidPhoneAlert || 'Not a valid mobile phone number';
}

/**
 * @deprecated
 * Use ´libphonenumber-js´ lib to validate phone numbers.
 * https://github.com/google/libphonenumber
 */
export function isValidPhoneNumber(value) {
  if (!value || isEmpty(value)) return false;

  const USPhoneTest = /^(\+[0-9]{1,2}[ -])?\(?([2-9]{1})([0-9]{2})\)?[-. ]?([2-9]{1})([0-9]{2})[-. ]?([0-9]{4})$/;
  const BRPhoneTest = /^\s*[+. ]?(\d{2}|\d{0})?[ ]?[(. ]?(\d{2}|\d{0})[). ]?[-. ]?(\d{5}|\d{4})[-. ]?(\d{4})[-. ]?\s*$/;
  const onlyNumbers = value.replace(/[^\d]/g, '');
  let equalSequenceTest = `(0{${onlyNumbers.length}})`;

  for (let i = 1; i < 10; i += 1) {
    equalSequenceTest += `|(${i}{${onlyNumbers.length}})`;
  }

  const regexTestResult = value.match(USPhoneTest) || value.match(BRPhoneTest);

  if (value && !onlyNumbers.match(`^${equalSequenceTest}$`) && regexTestResult) return false;

  return 'Not a valid US phone number';
}

export function isValidDate(value) {
  if (!value || isEmpty(value) || !isString(value)) return false;

  const onlyNumbers = value.replace(/[^\d]/g, '');
  let equalSequenceTest = `(0{${onlyNumbers.length}})`;

  for (let i = 1; i < 10; i += 1) {
    equalSequenceTest += `|(${i}{${onlyNumbers.length}})`;
  }

  if (
    value &&
    value.length === 10 &&
    !onlyNumbers.match(`^${equalSequenceTest}$`) &&
    moment(value, 'MM/DD/YYYY').isValid()
  ) {
    return false;
  }

  return 'Not a valid date';
}
/**
 * @deprecated
 * still here because is used inside embed file
 */
export function isValidDateCustom(field, message) {
  return (value, formValues, _, props) => {
    const date = get(formValues, field);
    const currentLocale = get(props, 'currentLocale');

    if (!date || isEmpty(date) || !isString(date)) return false;

    const onlyNumbers = date.replace(/[^\d]/g, '');
    let equalSequenceTest = `(0{${onlyNumbers.length}})`;

    for (let i = 1; i < 10; i += 1) {
      equalSequenceTest += `|(${i}{${onlyNumbers.length}})`;
    }

    const dobMask = currentLocale && currentLocale === 'pt-BR' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';

    const isDateValid =
      date &&
      date.length === 10 &&
      !onlyNumbers.match(`^${equalSequenceTest}$`) &&
      moment(date, dobMask).isValid() &&
      moment(date, dobMask).isSameOrAfter(
        moment()
          .year(1900)
          .startOf('year'),
      );

    if (isDateValid) {
      return false;
    }

    return message || 'Not a valid date';
  };
}

export function isPastDate(value) {
  if (value && value.length === 10 && moment(value, 'MM/DD/YYYY').isAfter(moment().startOf('day'))) {
    return 'Cannot be a future date';
  }

  return false;
}

export function validateAvailabilityBlocksTimeRange(name) {
  return (value, data) => {
    const fields = fromJS(data).toJS();
    let endTime = get(fields, 'endTime');
    let startTime = get(fields, 'startTime');

    if (startTime && endTime) {
      startTime = moment.utc(startTime);
      endTime = moment.utc(endTime);

      if (
        name === 'endTime' &&
        endTime.isSameOrBefore(startTime, 'hour') &&
        endTime.isSameOrBefore(startTime, 'minutes')
      ) {
        return 'Must be greater than the start time';
      }

      if (
        name === 'startTime' &&
        startTime.isSameOrAfter(endTime, 'hour') &&
        startTime.isSameOrAfter(endTime, 'minutes')
      ) {
        return 'Must be lower than the end time';
      }
    }

    return false;
  };
}

export function validatePatientContactValue(value, contact, data, invalidPhoneAlert, invalidEmailAlert) {
  const fields = fromJS(data).toJS();

  if (!fields.doNotContact && contact.type !== 'in-app') {
    return (
      (contact.type === 'email'
        ? isInvalidEmail(value, invalidEmailAlert)
        : validateMobileNumber(value, null, invalidPhoneAlert)) || required(value)
    );
  }

  return false;
}

export function validateReferralContactType(value, contact, data) {
  const fields = fromJS(data).toJS();

  if (!fields.patient) return required(value);

  return false;
}

export function validateReferralContactValue(value, contact, data) {
  const fields = fromJS(data).toJS();

  if (!fields.patient && contact.type !== 'in-app') {
    return (contact.type === 'email' ? isInvalidEmail(value) : isValidPhoneNumber(value)) || required(value);
  }

  return false;
}

export function requiredReferralPatientName(value, data) {
  const fields = fromJS(data).toJS();

  if (!fields.patient) return required(value);

  return false;
}

export function requiredWaitlistPatientName(value, data) {
  const fields = fromJS(data).toJS();

  if (fields.createNewPatient) return required(value);

  return false;
}

export function requiredWaitlistEhrId(value, data) {
  const fields = fromJS(data).toJS();

  if (fields.createNewPatient && fields.patientValues.ehrEnable) return required(value);

  return false;
}

export function validateWaitlistPatientDateOfBirth(value, data) {
  const fields = fromJS(data).toJS();

  if (fields.createNewPatient) return required(value) || isValidDate(value) || isPastDate(value);

  return false;
}

export function isUniqueCustomActionGroup(value, nestedData, data, { customActions, group }) {
  const groups = uniq(
    map(
      filter(customActions, (item) => item.group && item.group !== ''),
      'group',
    ),
  );

  if (value !== group && groups.indexOf(value) >= 0) return 'A group with this name already exists';

  return false;
}

export function requiredFutureAppointments(field, node) {
  return (value, formValues, _, props) => {
    const patient = get(formValues, field);
    const requireFutureAppointment = get(props, 'currentMaster.settings.cancellation.requireFutureAppointment');
    const appointments = get(props, 'appointments');

    if (patient && patient._id && requireFutureAppointment && (!appointments || appointments.length === 0)) {
      return node;
    }

    return false;
  };
}

export function shouldBeInFuture(type) {
  return (value, data) => {
    const fields = fromJS(data).toJS();
    const { date } = fields;
    const now = moment();

    if (type === 'date') {
      if (moment(value).isBefore(now.startOf('day'))) return 'Must be a future date';
    } else if (moment(`${moment(date).format('YYYY-MM-DD')} ${moment(value).format('HH:mm')}`).isBefore(now)) {
      return 'Must be a future time';
    }

    return false;
  };
}

export function externalProviderInfoRequired(isAdding) {
  return (value, data) => {
    const fields = fromJS(data).toJS();

    if (isAdding !== fields.isAdding) return false;

    return required(value);
  };
}

export function referringProviderFaxRequired(value, fields) {
  return (get(fields, 'sendReferralFax') && required(value)) || false;
}

export function addDepedentRequired(value, fields) {
  if (get(fields, 'addDependent', false)) {
    return required(value);
  }
  return false;
}

export function requiredSendAt(value, fields) {
  return (!get(fields, '_id') && get(fields, 'timing') === 'later' && required(value)) || false;
}

export function isValidSendAtTime(value, fields, aux, { currentUser }) {
  if (!value) return false;

  const sendAt = get(fields, 'sendAtDate');
  const status = get(fields, 'status', '');
  const timing = get(fields, 'timing', '');

  const { roles } = currentUser;
  const isAdmin = roles.find((role) => role === 'admin');

  // skip validation if broadcast is accepted, if is scheduled and pending for approval, is being processed or is not scheduled.
  if (
    status === 'accepted' ||
    timing === 'now' ||
    (status === 'scheduled' && sendAt.isBefore(moment().add(15, 'minutes'))) ||
    (isAdmin && /pending|declined/.test(status))
  ) {
    return false;
  }

  const time = isString(value) ? value.replace(/[^:\d]/g, '') : value.format('HH:mm');
  const startInterval = get(currentUser, 'settings.reminder.schedulableWindow.earliest');
  const endInterval = get(currentUser, 'settings.reminder.schedulableWindow.latest');
  const sendAtTime = time.split(':');

  if (!sendAt || !startInterval || !endInterval) return false;

  // add time to sendAt date
  sendAt.set({ hours: sendAtTime[0], minutes: sendAtTime[1] });

  if (sendAt.isBefore(moment().add(15, 'minutes'))) return 'Schedule time should be 15 minutes in the future';

  // set reminders window interval dates based on sendAt date
  const startDate = sendAt.clone().set({ hours: startInterval, minutes: 0 });
  const endDate = sendAt.clone().set({ hours: endInterval, minutes: 0 });

  return !sendAt.isSameOrAfter(startDate) || !sendAt.isSameOrBefore(endDate)
    ? `Please select a time between ${startDate.format('LT')} and ${endDate.format('LT')}`
    : false;
}

export function shouldNotContainsInvalidTerms(value) {
  if (!value) return false;

  const invalidTerms = `${process.env.REACT_APP_BROADCAST_INVALIDTERMS},${process.env.REACT_APP_BROADCAST_OFFENSIVEWORDS}`.split(
    ',',
  );
  if (getTermsIncluded(value, invalidTerms).length > 0) return 'Invalid terms used';

  return false;
}

export function containsOffensiveWords(value) {
  if (!value) return false;

  const offensiveWords = process.env.REACT_APP_BROADCAST_OFFENSIVEWORDS.split(',');

  return getTermsIncluded(value, offensiveWords).length > 0;
}

export function createValidator(rules) {
  return (formData, props) => {
    const data = formData.toJS();
    const errors = {};

    // this code can be improved to be recursive and support more levels of nested collections
    Object.keys(rules).forEach((key) => {
      if (get(rules[key], 'type') === 'collection') {
        const fields = get(rules[key], 'fields');
        const nestedData = get(data, key);

        map(nestedData, (item, index) => {
          const nestedErrors = {};

          Object.keys(fields).forEach((fieldKey) => {
            const validators = get(fields[fieldKey], 'validators', fields[fieldKey]);
            const rule = join([].concat(validators));
            const error = rule(get(item, fieldKey), item, data, props);

            if (error) nestedErrors[fieldKey] = error;
          });

          if (Object.keys(nestedErrors).length > 0) set(errors, `${key}[${index}]`, nestedErrors);
        });
      } else {
        // option to pass a key dependency to enable/disable validator
        const keyEnabled = get(rules[key], 'keyEnabled');
        if (!keyEnabled || get(data, keyEnabled)) {
          // run validators
          const validators = get(rules[key], 'validators', rules[key]);
          const rule = join([].concat(validators));
          const error = rule(get(data, key), data, data, props);
          if (error) {
            set(errors, key, error);
          }
        }
      }
    });

    return errors;
  };
}

export const validationSchemaPatientContactSms = Yup.string().test(
  'is-valid-phone',
  'Not a valid phone number',
  (value = '') => {
    try {
      return parsePhoneNumber(value, Country.US).isValid() || parsePhoneNumber(value, Country.Brazil).isValid();
    } catch (error) {
      return false;
    }
  },
);

export const validationSchemaPatientContactWhatsapp = Yup.string().test(
  'is-valid-bz-phone',
  'Not a valid phone number',
  (value = '') => {
    try {
      return parsePhoneNumber(value, Country.Brazil).isValid();
    } catch (error) {
      return false;
    }
  },
);

export const validationSchemaPatientContactEmail = Yup.string().test(
  'is-valid-email',
  'Not a valid Email',
  (value = '') => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value),
);
